import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';

import { useDeepEffect } from '@crossnokaye/ux-common';

import {
  getAnimaticReadingsIdentifiersAtom,
  getAnimaticSettingsIdentifiersAtom,
} from '@/features/equipment-browser/equipment-details/state/animatics.state';
import {
  equipmentLoadingAtom,
  equipmentResponseAtom,
} from '@/features/equipment-browser/equipment-details/state/equipment-response.state';
import { deduplicateSubscriptionIdentifiers } from '@/features/equipment-browser/equipment-details/state/identifiers.helpers';
import { readingIdentifiersAtom } from '@/features/equipment-browser/equipment-details/state/reading-identifiers.state';
import {
  sequencerEditorReadingsIdentifiersAtom,
  sequencerEditorSettingsIdentifiersAtom,
  siblingSequencerSettingsIdentifiersAtom,
} from '@/features/equipment-browser/equipment-details/state/sequencer-editor.state';
import {
  activeSequencerReadingsIdentifiersAtom,
  activeSequencerSettingsIdentifiersAtom,
  sequencerConfigsConditionReadingsIdentifiersAtom,
  sequencerConfigsConditionSettingsIdentifiersAtom,
} from '@/features/equipment-browser/equipment-details/state/sequencers.state';
import { settingsIdentifiersAtom } from '@/features/equipment-browser/equipment-details/state/setting-identifiers.state';
import { useSubscribeEBReadings } from '@/features/equipment-browser/subscriptions/use-subscribe-eb-readings';
import { useSubscribeEBSettings } from '@/features/equipment-browser/subscriptions/use-subscribe-eb-settings';
import { useGetEquipmentDetails } from '@/hooks/query-hooks/gaia-api/equipment-browser';
import { useCurrentFacility } from '@/hooks/use-current-facility';

type EquipmentResponseLoaderProps = {
  equipmentId: string;
};

export const useEquipmentResponseLoader = ({ equipmentId }: EquipmentResponseLoaderProps) => {
  const { agentId } = useCurrentFacility();
  const setCurrentEquipment = useSetAtom(equipmentResponseAtom);
  const setEquipmentLoading = useSetAtom(equipmentLoadingAtom);
  const readingsIdentifiers = useAtomValue(readingIdentifiersAtom);
  const settingsIdentifiers = useAtomValue(settingsIdentifiersAtom);
  const activeSequencerReadingsIdentifiers = useAtomValue(activeSequencerReadingsIdentifiersAtom);
  const activeSequencerSettingsIdentifiers = useAtomValue(activeSequencerSettingsIdentifiersAtom);
  const sequencerConditionReadingsIdentifiers = useAtomValue(
    sequencerConfigsConditionReadingsIdentifiersAtom
  );
  const sequencerConditionSettingsIdentifiers = useAtomValue(
    sequencerConfigsConditionSettingsIdentifiersAtom
  );
  const sequencerEditorReadingsIdentifiers = useAtomValue(sequencerEditorReadingsIdentifiersAtom);
  const sequencerEditorSettingsIdentifiers = useAtomValue(sequencerEditorSettingsIdentifiersAtom);
  const siblingSequencerSettingsIdentifiers = useAtomValue(siblingSequencerSettingsIdentifiersAtom);

  const animaticReadingsIdentifiers = useAtomValue(getAnimaticReadingsIdentifiersAtom);
  const animaticSettingsIdentifiers = useAtomValue(getAnimaticSettingsIdentifiersAtom);

  const allReadingsIdentifiers = useMemo(() => {
    const combined = [
      ...(readingsIdentifiers ?? []),
      ...(sequencerConditionReadingsIdentifiers ?? []),
      ...(activeSequencerReadingsIdentifiers ?? []),
      ...(sequencerEditorReadingsIdentifiers ?? []),
      ...(animaticReadingsIdentifiers ?? []),
    ];

    return deduplicateSubscriptionIdentifiers(combined);
  }, [
    readingsIdentifiers,
    sequencerConditionReadingsIdentifiers,
    activeSequencerReadingsIdentifiers,
    sequencerEditorReadingsIdentifiers,
    animaticReadingsIdentifiers,
  ]);

  const allSettingsIdentifiers = useMemo(() => {
    const combined = [
      ...(settingsIdentifiers ?? []),
      ...(sequencerConditionSettingsIdentifiers ?? []),
      ...(activeSequencerSettingsIdentifiers ?? []),
      ...(sequencerEditorSettingsIdentifiers ?? []),
      ...(siblingSequencerSettingsIdentifiers ?? []),
      ...(animaticSettingsIdentifiers ?? []),
    ];

    return deduplicateSubscriptionIdentifiers(combined);
  }, [
    settingsIdentifiers,
    sequencerConditionSettingsIdentifiers,
    activeSequencerSettingsIdentifiers,
    sequencerEditorSettingsIdentifiers,
    siblingSequencerSettingsIdentifiers,
    animaticSettingsIdentifiers,
  ]);

  // Subscribe to the readings and settings
  useSubscribeEBReadings(allReadingsIdentifiers);
  useSubscribeEBSettings(allSettingsIdentifiers);

  const { data, isLoading } = useGetEquipmentDetails({
    agentId,
    deviceId: equipmentId,
  });

  useDeepEffect(() => {
    setCurrentEquipment(data);
  }, [data]);

  useDeepEffect(() => {
    setEquipmentLoading(isLoading);
  }, [isLoading]);

  return { data, isLoading };
};
